import { ozmapService } from '../services/ozmapService';
import { haversineDistance } from './geolocationUtils';

// --- CACHE MODULE ---
const CACHE_TTL = 10 * 60 * 1000; // 10 minutos
const ozmapCache = new Map();

const getFromCache = (key) => {
    const entry = ozmapCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_TTL) {
        ozmapCache.delete(key);
        return null;
    }
    return entry.data;
};

const setInCache = (key, data) => {
    ozmapCache.set(key, { timestamp: Date.now(), data });
};

/**
 * Calculates the center and radius (coverage) for a set of poles.
 * @param {Array} poles - Array of pole objects with latitude and longitude.
 * @returns {Object|null} - { center: {lat, lng}, radius: number } or null if invalid.
 */
const calculateCoverage = (poles) => {
    if (!poles || poles.length === 0) return null;

    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;
    let hasValid = false;

    poles.forEach(p => {
        const lat = parseFloat(p.latitude);
        const lng = parseFloat(p.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            hasValid = true;
        }
    });

    if (!hasValid) return null;

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    const center = { lat: centerLat, lng: centerLng };

    // Calculate radius: distance to the farthest corner from center
    // Approximate by taking max diff
    const corner = { lat: maxLat, lng: maxLng };
    // Add 200m margin
    const radius = haversineDistance(center, corner) + 200;

    return { center, radius };
};

/**
 * Fetches Ozmap data (CTOs, CEOs) for a list of projects in the background.
 * Optimized with Cache, Select, and Grouping.
 * Cables are NO LONGER fetched for performance reasons.
 * @param {Array} projects - List of projects to fetch data for.
 * @returns {Promise<Object>} - { ctos: [], ceos: [], cables: [] } (cables will be empty)
 */
export const fetchOzmapDataForProjects = async (projects) => {
    // 1. Group by City to optimize cache usage and potentially merge requests (future)
    const projectsByCity = new Map();
    const relevantProjects = (projects || []).filter(p =>
        p.cidade &&
        p.postes &&
        Array.isArray(p.postes) &&
        p.postes.some(pole => pole.latitude && pole.longitude)
    );

    relevantProjects.forEach(p => {
        const city = p.cidade;
        if (!projectsByCity.has(city)) {
            projectsByCity.set(city, []);
        }
        projectsByCity.get(city).push(p);
    });

    const boxesMap = new Map();
    const tasks = [];

    // Helper to normalize coordinates
    const normalizeBox = (box) => {
        let lat = box.lat;
        let lng = box.lng;

        // If 'coords' is present (which it should be due to 'select'), extract lat/lng
        if (box.coords) {
            if (Array.isArray(box.coords) && box.coords.length >= 2) {
                // Ozmap returns [lng, lat]
                lng = box.coords[0];
                lat = box.coords[1];
            } else if (typeof box.coords === 'object') {
                lat = box.coords.lat;
                lng = box.coords.lng;
            }
        }
        return { ...box, lat, lng };
    };

    // 2. Process each city and project, computing coverage individually to prevent massive search radii
    for (const [city, cityProjects] of projectsByCity) {
        for (const project of cityProjects) {
            const poles = (project.postes || []).filter(p => p.latitude && p.longitude);
            if (!poles || poles.length === 0) continue;

            const updatedAtMillis = project.updatedAt ? (typeof project.updatedAt.toMillis === 'function' ? project.updatedAt.toMillis() : new Date(project.updatedAt).getTime()) : 0;
            const cacheKey = `boxes_only_${project.id || project._id}_${updatedAtMillis}`;

            const cachedData = getFromCache(cacheKey);
            if (cachedData) {
                if (cachedData.boxes && Array.isArray(cachedData.boxes)) {
                    cachedData.boxes.forEach(box => {
                        const normalized = normalizeBox(box);
                        const id = normalized._id || normalized.id;
                        if (id) {
                            boxesMap.set(id, normalized);
                        }
                    });
                }
                continue;
            }

            tasks.push(async () => {
                try {
                    const projectId = await ozmapService.getProjectByName(city);
                    const coverage = calculateCoverage(poles);
                    if (!coverage) return;

                    // Fetch optimized boxes (limiting select fields to reduce payload size)
                    const boxes = await ozmapService.fetchBoxesByRadius(projectId, coverage.center, coverage.radius, 'id name coords boxType');

                    setInCache(cacheKey, { boxes });

                    boxes.forEach(box => {
                        const normalized = normalizeBox(box);
                        const id = normalized._id || normalized.id;
                        if (id) {
                            boxesMap.set(id, normalized);
                        }
                    });
                } catch (err) {
                    console.warn(`[OzmapFetch] Error processing project ${project.id || 'unknown'} in ${city}:`, err.message);
                }
            });
        }
    }

    // 3. Execute tasks with concurrency limit
    if (tasks.length > 0) {
        // Increase concurrency to fetch independent project areas rapidly
        const CONCURRENCY_LIMIT = 5;
        const executeTasks = async () => {
            const executing = new Set();
            for (const task of tasks) {
                const p = task().then(() => executing.delete(p));
                executing.add(p);
                if (executing.size >= CONCURRENCY_LIMIT) {
                    await Promise.race(executing);
                }
            }
            await Promise.all(executing);
        };
        try {
            await executeTasks();
        } catch (e) {
            console.error("[OzmapFetch] Error executing tasks:", e);
        }
    }

    // 4. Process and Categorize
    const ctos = [];
    const ceos = [];

    for (const box of boxesMap.values()) {
        const name = (box.name || '').toUpperCase();
        if (name.includes('CTO')) {
            ctos.push(box);
        } else if (name.includes('CEO')) {
            ceos.push(box);
        }
    }

    return { ctos, ceos, cables: [] };
};
